/**
 * Every solver in src/math returns its answer *and* the worked steps that
 * produced it. That is the whole architectural bet of this app: the drill's
 * "walk me through it" and the calculator's "show the math" render the same
 * Step[] from the same function, so an explanation can never drift out of
 * sync with the number it explains.
 */

export type StepKind =
  /** State the formula before touching any numbers. */
  | 'formula'
  /** Put the given values into the formula. */
  | 'substitute'
  /** One arithmetic move. */
  | 'compute'
  /** The answer, in the units asked for. */
  | 'result'
  /** Context — a unit conversion, a convention, a thing that trips people up. */
  | 'note';

export interface Step {
  readonly kind: StepKind;
  readonly text: string;
}

export interface Solved<T> {
  readonly value: T;
  readonly steps: readonly Step[];
}

export const step = (kind: StepKind, text: string): Step => ({ kind, text });

/**
 * Solvers reject impossible input loudly rather than returning NaN. A NaN that
 * reaches the UI becomes a confidently-displayed wrong answer, which is the
 * single worst failure mode this app has.
 */
export class ShopMathError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ShopMathError';
  }
}

export function require(condition: boolean, message: string): asserts condition {
  if (!condition) throw new ShopMathError(message);
}

export function requirePositive(value: number, label: string): void {
  require(
    Number.isFinite(value) && value > 0,
    `${label} must be a positive number (got ${value})`,
  );
}

export function requireFinite(value: number, label: string): void {
  require(Number.isFinite(value), `${label} must be a finite number (got ${value})`);
}
